import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs';

import { LandingCheckoutService } from './landing-checkout.service';
import { BillingPlanKey } from './pricing-cart.models';
import { PricingCartService } from './pricing-cart.service';

@Component({
  selector: 'app-pricing-cart-drawer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="cart-drawer-shell" [class.cart-drawer-shell-open]="cart.drawerOpen()">
      <div class="cart-drawer-backdrop" (click)="close()" aria-hidden="true"></div>

      <aside class="cart-drawer glass-card" role="dialog" aria-modal="true" aria-label="Build your plan cart">
        <div class="cart-drawer-header">
          <div>
            <div class="eyebrow">Build your plan</div>
            <h2 class="subsection-title">Monthly B2B cart</h2>
          </div>

          <button class="cart-close-button" type="button" (click)="close()" aria-label="Close cart">
            <span aria-hidden="true">x</span>
          </button>
        </div>

        <div class="cart-empty-state" *ngIf="!cart.hasItems()">
          <strong>Your plan is empty.</strong>
          <p>{{ emptyStateMessage() }}</p>
          <button class="primary-button" type="button" (click)="close()">Continue building</button>
        </div>

        <ng-container *ngIf="cart.hasItems()">
          <div class="cart-line-list">
            <article class="cart-line-item" *ngFor="let line of cart.lines()">
              <div class="cart-line-head">
                <div>
                  <strong class="cart-line-name">{{ line.name }}</strong>
                  <div class="cart-line-price">{{ line.priceLabel }}</div>
                </div>

                <button class="cart-remove-button" type="button" (click)="remove(line.key)">Remove</button>
              </div>

              <div class="cart-line-actions">
                <div class="quantity-stepper quantity-stepper-compact">
                  <button type="button" (click)="decrement(line.key)" aria-label="Decrease quantity">-</button>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    inputmode="numeric"
                    [value]="line.quantity"
                    (input)="updateQuantity(line.key, $event)"
                    aria-label="Number of users"
                  />
                  <button type="button" (click)="increment(line.key)" aria-label="Increase quantity">+</button>
                </div>

                <strong class="cart-line-total">{{ cart.formatAmount(line.lineTotal) }} / month</strong>
              </div>
            </article>
          </div>

          <label class="contact-field cart-checkout-field">
            <span>Company name</span>
            <input
              class="cart-checkout-input"
              type="text"
              autocomplete="organization"
              placeholder="Acme QA"
              [value]="cart.companyName()"
              (input)="updateCompanyName($event)"
            />
          </label>

          <label class="contact-field cart-checkout-field">
            <span>Billing email</span>
            <input
              class="cart-checkout-input"
              type="email"
              autocomplete="email"
              placeholder="team@company.com"
              [value]="cart.checkoutEmail()"
              (input)="updateEmail($event)"
            />
          </label>

          <div class="cart-caption">
            {{ checkoutCaption() }}
          </div>

          <div class="contact-feedback contact-feedback--error" *ngIf="error()">
            {{ error() }}
          </div>

          <footer class="cart-summary">
            <div class="cart-summary-row">
              <span>Total seats</span>
              <strong>{{ cart.totalSeats() }}</strong>
            </div>
            <div class="cart-summary-row cart-summary-row-strong">
              <span>Total monthly</span>
              <strong>{{ cart.formatAmount(cart.totalMonthly()) }} / month</strong>
            </div>
          </footer>

          <div class="cart-footer-actions">
            <button class="primary-button primary-button--wide" type="button" [disabled]="checkoutDisabled()" (click)="proceedToCheckout()">
              {{ processing() ? 'Redirecting...' : 'Proceed to checkout' }}
            </button>
            <button class="secondary-button primary-button--wide" type="button" (click)="talkToSales()">
              Talk to sales
            </button>
          </div>
        </ng-container>
      </aside>
    </div>
  `
})
export class PricingCartDrawerComponent {
  private readonly router = inject(Router);
  private readonly checkoutService = inject(LandingCheckoutService);

  readonly cart = inject(PricingCartService);
  readonly processing = signal(false);
  readonly error = signal<string | null>(null);
  readonly checkoutDisabled = computed(() => this.processing() || !this.cart.hasItems());
  readonly emptyStateMessage = computed(() => {
    if (this.cart.hasSelfServePlans() && this.cart.hasSalesAssistedPlans()) {
      return 'Add at least one checkout-ready plan to unlock Stripe. Sales-assisted plans remain available from the pricing cards and contact flow.';
    }

    if (this.cart.hasSelfServePlans()) {
      return 'Add at least one checkout-ready plan to unlock Stripe checkout.';
    }

    if (this.cart.hasSalesAssistedPlans()) {
      return 'The current public plans are sales-assisted only. Use the pricing cards or contact flow to request rollout.';
    }

    return 'No checkout-ready plan is currently available.';
  });
  readonly checkoutCaption = computed(() => {
    if (this.cart.hasSelfServePlans() && this.cart.hasSalesAssistedPlans()) {
      return 'Stripe checkout is created for the self-serve plans in this cart. Sales-assisted plans stay handled through commercial follow-up.';
    }

    if (this.cart.hasSelfServePlans()) {
      return 'Stripe checkout is created with this email, then access confirmation is sent after payment.';
    }

    return 'Checkout is unavailable because the current catalog is configured for sales-assisted rollout.';
  });

  close(): void {
    this.error.set(null);
    this.cart.closeDrawer();
  }

  increment(planKey: BillingPlanKey): void {
    this.error.set(null);
    this.cart.increment(planKey);
  }

  decrement(planKey: BillingPlanKey): void {
    this.error.set(null);
    this.cart.decrement(planKey);
  }

  remove(planKey: BillingPlanKey): void {
    this.error.set(null);
    this.cart.remove(planKey);
  }

  updateQuantity(planKey: BillingPlanKey, event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.error.set(null);
    this.cart.setQuantity(planKey, target?.value ?? 0);
  }

  updateEmail(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.error.set(null);
    this.cart.setCheckoutEmail(target?.value ?? '');
  }

  updateCompanyName(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.error.set(null);
    this.cart.setCompanyName(target?.value ?? '');
  }

  talkToSales(): void {
    this.error.set(null);
    this.cart.closeDrawer();
    void this.router.navigate(['/contact']);
  }

  proceedToCheckout(): void {
    if (this.checkoutDisabled()) {
      return;
    }

    const companyName = this.cart.companyName().trim();
    if (!companyName) {
      this.error.set('Enter the company name before continuing to Stripe.');
      return;
    }

    const email = this.cart.checkoutEmail().trim();
    if (!this.isValidEmail(email)) {
      this.error.set('Enter a valid billing email before continuing to Stripe.');
      return;
    }

    this.processing.set(true);
    this.error.set(null);

    this.checkoutService.createCheckoutSession({
      companyName,
      email,
      requestedPlans: this.cart.lines().map((line) => ({
        planKey: line.key,
        quantity: line.quantity
      }))
    })
      .pipe(take(1))
      .subscribe({
        next: ({ url }) => {
          window.location.href = url;
        },
        error: (error) => {
          this.processing.set(false);
          this.error.set(
            error?.error?.message ||
            'Stripe checkout could not be started right now. Please try again or talk to sales.'
          );
        }
      });
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }
}
